import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCompanyServices } from '../api/Installation';

// Define the CompanyService interface inside the slice
interface CompanyService {
  installation_id: string;
  owner_reg_number: string;
  owner_name: string;
  service_type: string;
  readable_name: string;
  details: {
    ean: string;
  };
}

interface FetchCompanyServicesArgs {
  companyNumber: string;
  token: string;
}

interface CompanyServiceState {
  services: CompanyService[];
  loading: boolean;
  error: string | null;
}

const initialState: CompanyServiceState = {
  services: [],
  loading: false,
  error: null,
};

// Async thunk to fetch company services
export const fetchCompanyServices = createAsyncThunk<
  CompanyService[], // Expected to return an array of CompanyService
  FetchCompanyServicesArgs,
  { rejectValue: string }
>('companyService/fetchCompanyServices', async ({ companyNumber, token }, { rejectWithValue }) => {
  try {
    const data = await getCompanyServices(companyNumber, token);
    return data; // data is CompanyService[]
  } catch (err) {
    if (err instanceof Error) {
      return rejectWithValue(err.message);
    } else {
      return rejectWithValue('An unknown error occurred');
    }
  }
});

const companyServiceSlice = createSlice({
  name: 'companyService',
  initialState,
  reducers: {
    resetCompanyServiceState: (state) => {
      state.services = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload;
      })
      .addCase(fetchCompanyServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An unknown error occurred';
        console.error('Error in fetchCompanyServices:', state.error);
      });
  },
});

export const { resetCompanyServiceState } = companyServiceSlice.actions;
export default companyServiceSlice.reducer;
